<script lang="ts" setup>
import { TRACKING_EVENTS } from "@constant"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useFormStore } from "@stores/form.store"
import {
    optionsChauffage,
    optionsEauChaudeSanitaire,
} from "./typeLogement.options"
import { toTypedSchema } from "@vee-validate/yup"
import { useField, useForm } from "vee-validate"
import { inject, onActivated, onDeactivated, ref } from "vue"
import { object, string } from "yup"

const { snapshot, send } = useFormStore()

const { $track } = inject<TrackingPlugin>("tracking")!

const options = ref([
    {
        label: "Je vis dans une maison",
        value: "maison",
    },
    {
        label: "Je vis dans un appartement",
        value: "appartement",
    },
])

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            type_logement: string()
                .required("Sélectionnez un type de logement")
                .default(snapshot.value.context.type_logement),
            // `oneOf` : une valeur absente de la liste (par exemple un ancien
            // « individuel_et_collectif » rechargé depuis le dossier) ne
            // repart pas vers l'API, qui la refuserait en 400 : le ménage
            // doit choisir de nouveau (DEV-2430).
            type_chauffage: string()
                .nullable()
                .when("type_logement", {
                    is: (val: string) => val === "appartement",
                    then: (schema) =>
                        schema
                            .required("Sélectionnez un type de chauffage")
                            .oneOf(
                                optionsChauffage.map((o) => o.value),
                                "Sélectionnez un type de chauffage"
                            ),
                })
                .default(snapshot.value.context.type_chauffage),
            type_eau_chaude_sanitaire: string()
                .nullable()
                .when("type_logement", {
                    is: (val: string) => val === "appartement",
                    then: (schema) =>
                        schema.required("Sélectionnez un type d'eau chaude"),
                })
                .default(snapshot.value.context.type_eau_chaude_sanitaire),
        })
    ),
})

const { value: type_logement } = useField("type_logement")
const { value: chauffage } = useField("type_chauffage")
const { value: eau_chaude_sanitaire } = useField("type_eau_chaude_sanitaire")

function onError() {
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: snapshot.value.context.formId,
        step: snapshot.value.context.step + 1,
    })
}

function onSuccess(values: any) {
    send({
        type: "UPDATE_FIELD",
        field: "type_logement",
        value: values.type_logement,
    })
    send({
        type: "UPDATE_FIELD",
        field: "type_chauffage",
        value: values.type_logement === "maison" ? null : values.type_chauffage,
    })
    send({
        type: "UPDATE_FIELD",
        field: "type_eau_chaude_sanitaire",
        value:
            values.type_logement === "maison"
                ? null
                : values.type_eau_chaude_sanitaire,
    })

    send({
        type: "SUBMIT",
    })
}

const onSubmit = handleSubmit(onSuccess, onError)

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})

function onUpdateTypeLogement(value: string) {
    // Si on est dans une maison, on met à null les champs de chauffage et ECS
    if (value === "maison") {
        chauffage.value = null
        eau_chaude_sanitaire.value = null
    }
}
</script>

<template>
    <FormCard
        class="form-type-logement"
        title="L'essentiel"
        sub-title="Type de logement"
        @submit="
            () => {
                onSubmit()
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-type-logement',
                    buttonName: 'Suivant',
                })
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-type-logement',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="
            snapshot.matches({ Step2: { FormTypeLogement: 'updateField' } })
        "
    >
        <!-- Sélection entre recherche ou saisie manuel -->
        <BaseSelectButton
            class="form-type-logement__select"
            v-model="type_logement"
            :allow-empty="false"
            :options="options"
            option-label="label"
            option-value="value"
            :pt="{
                pcToggleButton: {
                    root: 'custom-togglebutton',
                    label: 'custom-togglebutton-label',
                },
            }"
            @update:modelValue="onUpdateTypeLogement"
        />

        <!-- Pour le cas d'un appartement, formulaire sur les types de chauffage et ECS -->
        <fieldset v-if="type_logement === 'appartement'">
            <legend class="text-primary-800 font-bold mb-4">
                Vous avez du chauffage :
            </legend>
            <BaseCheckboxList
                :options="optionsChauffage"
                name="type_chauffage"
                option-label="label"
                option-value="value"
                v-model="chauffage"
                :error="errors.type_chauffage"
                input-id="type_chauffage"
                radio
            />
        </fieldset>
        <fieldset v-if="type_logement === 'appartement'">
            <legend class="text-primary-800 font-bold mb-4">
                Votre eau chaude sanitaire est-elle :
            </legend>
            <BaseCheckboxList
                :options="optionsEauChaudeSanitaire"
                name="type_eau_chaude_sanitaire"
                option-label="label"
                option-value="value"
                v-model="eau_chaude_sanitaire"
                :error="errors.type_eau_chaude_sanitaire"
                input-id="type_eau_chaude_sanitaire"
                radio
            />
        </fieldset>
    </FormCard>
</template>

<style scoped>
.form-type-logement {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }
}
</style>
